import { Component, EventEmitter, Input, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IMarker } from '../../ranch-settings/interfaces';
import { RanchCreateWeatherStationDialog } from '../dialogs/weather-station-dialog';

@Component({
	moduleId: module.id,
	selector: 'ranch-create-weather-stations-step',
	templateUrl: 'step-weather-stations.html',
	styleUrls: ['../ranch-create.scss']
})

export class RanchCreateWeatherStationsStepComponent {
	@Input()
	public form: FormArray;

	@Input()
	public ranchCoordinates: string;

	@Input()
	public subscriptions$: Subject<boolean>;

	@Output()
	public nextStep: EventEmitter<number> = new EventEmitter<number>();

	@Output()
	public onBack: EventEmitter<void> = new EventEmitter<void>();

	@Output()
	public onCancel: EventEmitter<void> = new EventEmitter<void>();

	public get weatherStationControls(): AbstractControl[] {
		if (!this.form) {
			return [];
		}

		return this.form.controls;
	}

	constructor(
		private _dialog: MatDialog,
		private _fb: FormBuilder
	) { }

	public back(): void {
		this.onBack.emit();
	}

	public cancel(): void {
		this.onCancel.emit();
	}

	public deleteWeatherStation(index: number, event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}

		this.form.removeAt(index);
		this.form.markAsDirty();
		this._resetPriorities();
	}

	public moveWeatherStationDown(index: number, event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}

		if (index >= this.form.length - 1) {
			return;
		}

		this._moveWeatherStation(index, index + 1);
	}

	public moveWeatherStationUp(index: number, event?: MouseEvent): void {
		if (event) {
			event.stopPropagation();
		}

		if (index <= 0) {
			return;
		}

		this._moveWeatherStation(index, index - 1);
	}

	public next(): void {
		this.form.markAllAsTouched();
		this.form.updateValueAndValidity();

		if (!this.form.valid) {
			return;
		}

		this.nextStep.emit(3);
	}

	public openAddWeatherStationDialog(): void {
		this._dialog.open(RanchCreateWeatherStationDialog, {
			width: '880px',
			data: {
				ranchCoordinates: this.ranchCoordinates,
				selectedStationIds: this._getSelectedStationIds()
			}
		}).afterClosed().pipe(takeUntil(this.subscriptions$)).subscribe((stations: IMarker[]) => {
			if (!stations || stations.length === 0) {
				return;
			}

			for (let station of stations) {
				this.form.push(this._initializeForm(station));
			}

			this.form.markAsDirty();
			this._resetPriorities();
		});
	}

	private _initializeForm(station: IMarker): FormGroup {
		return this._fb.group({
			StationId: [station.Id, Validators.required],
			Name: [station.Name, Validators.required],
			Distance: [station.Distance],
			Priority: [this.form.length + 1, Validators.required]
		});
	}

	private _getSelectedStationIds(): number[] {
		return this.form.controls
			.map((control) => control.get('StationId').value);
	}

	private _moveWeatherStation(fromIndex: number, toIndex: number): void {
		let control = this.form.at(fromIndex);

		this.form.removeAt(fromIndex);
		this.form.insert(toIndex, control);
		this.form.markAsDirty();
		this._resetPriorities();
	}

	private _resetPriorities(): void {
		this.form.controls.forEach((control, index) => {
			control.get('Priority').setValue(index + 1);
		});
	}
}
